import { env } from "../config/env";
import { createClient } from "./http";

const api = createClient(env.WAREHOUSE_BASE_URL);

function appendIfPresent(formData, key, value) {
  if (value === undefined || value === null || value === "") return;
  formData.append(key, value);
}

function normalizeArray(value) {
  return Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
}

function normalizeContexts(value) {
  const raw = normalizeArray(value).map((item) => String(item || "").trim().toUpperCase());
  const mapped = raw
    .map((item) => {
      if (["BOTH", "ALL", "GLOBAL"].includes(item)) return "ALL";
      if (["GROCERY", "GROCERIES", "MART", "STORE"].includes(item)) return "GROCERY";
      if (["FOOD", "RESTAURANT", "RESTAURANTS"].includes(item)) return "FOOD";
      return null;
    })
    .filter(Boolean);

  if (mapped.includes("ALL")) return ["ALL"];

  const unique = Array.from(new Set(mapped));
  return unique.length ? unique : ["GROCERY"];
}

function buildFilters(payload) {
  return {
    contexts: normalizeContexts(payload?.filters?.contexts),
    warehouseIds: normalizeArray(payload?.filters?.warehouseIds),
    cities: normalizeArray(payload?.filters?.cities),
    states: normalizeArray(payload?.filters?.states),
  };
}

export const postersService = {
  async list({ page = 1, limit = 10, q = "", isActive = "", context = "" } = {}) {
    return api.get("/api/admin/posters", {
      params: { page, limit, q, isActive, context },
    });
  },

  async getById(id) {
    return api.get(`/api/admin/posters/${id}`);
  },

  async getFilterOptions(params = {}) {
    return api.get("/api/admin/posters/filter-options", { params });
  },

  async create(payload) {
    const formData = new FormData();

    formData.append("title", String(payload?.title || "").trim());
    appendIfPresent(formData, "description", payload?.description?.trim());
    appendIfPresent(formData, "ctaLink", payload?.ctaLink?.trim());
    appendIfPresent(formData, "targetType", payload?.targetType || "ALL");
    appendIfPresent(formData, "placement", payload?.placement || "TOP");
    formData.append("isActive", String(payload?.isActive ?? true));

    if (payload?.startAt) formData.append("startAt", payload.startAt);
    if (payload?.endAt) formData.append("endAt", payload.endAt);

    const filters = buildFilters(payload);
    formData.append("filters", JSON.stringify(filters));

    if (payload?.image instanceof File) {
      formData.append("image", payload.image);
    }

    return api.post("/api/admin/posters", formData);
  },

  async update(id, payload) {
    const formData = new FormData();

    formData.append("title", String(payload?.title || "").trim());
    appendIfPresent(formData, "description", payload?.description?.trim());
    appendIfPresent(formData, "ctaLink", payload?.ctaLink?.trim());
    appendIfPresent(formData, "targetType", payload?.targetType || "ALL");
    appendIfPresent(formData, "placement", payload?.placement || "TOP");

    if (payload?.isActive !== undefined) {
      formData.append("isActive", String(payload.isActive));
    }

    if (payload?.startAt) {
      formData.append("startAt", payload.startAt);
    } else if (payload?.startAt === null) {
      formData.append("startAt", "");
    }

    if (payload?.endAt) {
      formData.append("endAt", payload.endAt);
    } else if (payload?.endAt === null) {
      formData.append("endAt", "");
    }

    const filters = buildFilters(payload);
    formData.append("filters", JSON.stringify(filters));

    if (payload?.image instanceof File) {
      formData.append("image", payload.image);
    }

    return api.patch(`/api/admin/posters/${id}`, formData);
  },

  async setStatus(id, isActive) {
    return api.patch(`/api/admin/posters/${id}/status`, { isActive });
  },

  async remove(id) {
    return api.del(`/api/admin/posters/${id}`);
  },

  async stats(id) {
    return api.get(`/api/admin/posters/${id}/stats`);
  },
};

export function buildPosterImageUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = env.WAREHOUSE_BASE_URL || "";
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}